/**
 * legdrag.ts — ONE drag implementation for chart price levels.
 *
 * WHY IT LIVES HERE AND NOT IN A PRIMITIVE
 * ----------------------------------------
 * `TicketPrimitive` owned this code and only the pre-trade ticket could be dragged.
 * The LIVE position's stop and target now need exactly the same gesture on all four
 * panes (`levelprimitive.ts`), and a second implementation of "which line is the
 * cursor on, and what price is that pixel" is the second-source-of-truth this app has
 * already paid for twice. So the logic was LIFTED, not copied: both primitives drive
 * this class, and a fix to the hit test or to the pan suppression lands on both.
 *
 * PIXELS ARE CONVERTED BY THE HOST'S OWN SERIES, NEVER BY A SHARED SCALE. Panes hold
 * independent price windows (`main.ts:419` — a past bug left 1W and 1D on BTC's window
 * with SOL's bars off screen), so `coordinateToPrice` is asked of the series that DREW
 * the line the user grabbed. That is the whole reason `DragHost.series` is a getter
 * rather than a price range passed in.
 *
 * IT DECIDES NOTHING ABOUT MONEY. It reports "leg X is now at price P" to its owner.
 * No clamping, no validation, no write — a drag is a gesture, not an intent
 * (`ticket.ts:251`), and the confirm/refuse rules live where the risk gates are.
 *
 * NO DOM IN THE CLASS, on purpose: the pointer plumbing is `wireLegDrag` below, so the
 * arithmetic that turns a pixel into a stop price is testable in this repo's DOM-less
 * vitest environment instead of only through a browser.
 */

/** How close (CSS px) the cursor must be to a line to grab it. */
export const GRAB_TOLERANCE_PX = 6;

/** A level as far as dragging is concerned. Presentation lives in the primitives. */
export type DragLeg = { id: string; price: number; draggable: boolean };

/** The two projection calls we need off a lightweight-charts series. Declared
 *  structurally so the class can be driven by a fake in a test — the library's own
 *  branded `Coordinate`/`BarPrice` types are narrowed by `num` below anyway. */
export type DragSeries = {
  priceToCoordinate(price: number): unknown;
  coordinateToPrice(coordinate: number): unknown;
};

export type DragCallbacks = {
  /** Live during a drag — fires on every pointer move. */
  onDrag: (id: string, price: number) => void;
  /** Once, on release. Still NOT a write: see the file header. */
  onDrop?: (id: string, price: number) => void;
};

export type DragHost = {
  series: () => DragSeries | null;
  legs: () => readonly DragLeg[];
  callbacks: () => DragCallbacks | null;
  /** Suppress (false) / restore (true) the chart's own pan+zoom for the drag. */
  setScroll: (enabled: boolean) => void;
  /** Ask for a redraw when hover/drag emphasis changes. */
  requestUpdate: () => void;
};

const num = (v: unknown): number | null => {
  const n = Number(v);
  return v === null || v === undefined || !Number.isFinite(n) ? null : n;
};

const usable = (p: number | null): p is number => p !== null && Number.isFinite(p) && p > 0;

/** True on a touch-first device (`(pointer: coarse)`). Read on each press/hover, never
 *  polled. A missing `matchMedia` (this repo's DOM-less vitest) counts as fine. */
export const coarsePointer = (): boolean =>
  globalThis.matchMedia?.('(pointer: coarse)')?.matches === true;

export class LegDrag {
  /** id under the cursor, for emphasis. */
  hovered: string | null = null;
  /** id being dragged, or null. */
  dragging: string | null = null;

  constructor(
    private readonly host: DragHost,
    private readonly coarse: () => boolean = coarsePointer,
  ) {}

  /** The DRAGGABLE leg nearest a CSS y coordinate, or null.
   *
   *  x is not consulted: the lines span the pane, so proximity in y is the whole
   *  test. Non-draggable legs (liquidation — a consequence of leverage, not an
   *  input) are skipped here, which is what makes them ungrabbable rather than
   *  merely unlabelled. */
  legAt(y: number): DragLeg | null {
    // NO DRAG ON A COARSE POINTER (2026-10-18, intent 261018-mobile-v1, FR3.4). A finger
    // cannot hit a 6 px band (GRAB_TOLERANCE_PX), and a misread drag stages a wrong price
    // one step from the book. This is the one lookup behind both `down` (press) and `hover`
    // (the primitives' hitTest cursor), and both ticket legs and live SL/TP lines build a
    // LegDrag, so one line here covers every entry point: the hub's pointerdown finds no
    // leg, does not preventDefault, and the chart pans. Prices change only in the form.
    if (this.coarse()) return null;
    const series = this.host.series();
    if (!series) return null;
    let best: { leg: DragLeg; d: number } | null = null;
    for (const leg of this.host.legs()) {
      if (!leg.draggable) continue;
      const ly = num(series.priceToCoordinate(leg.price));
      if (ly === null) continue;
      const d = Math.abs(ly - y);
      if (d <= GRAB_TOLERANCE_PX && (best === null || d < best.d)) best = { leg, d };
    }
    return best?.leg ?? null;
  }

  /** Hover bookkeeping. Returns the id under the cursor so a `hitTest` can hand the
   *  library a `cursorStyle` — without one nothing suggests the line can be moved. */
  hover(y: number): string | null {
    const next = this.legAt(y)?.id ?? null;
    if (next !== this.hovered) {
      this.hovered = next;
      this.host.requestUpdate();
    }
    return next;
  }

  /** Grab. Returns whether a leg was taken, so the caller knows to swallow the event. */
  down(y: number): boolean {
    const leg = this.legAt(y);
    if (!leg) return false;
    this.dragging = leg.id;
    // Otherwise dragging a stop also pans the time scale: the line looks stuck while
    // the candles slide underneath it.
    this.host.setScroll(false);
    this.host.requestUpdate();
    return true;
  }

  /** Pointer move. Converts the pixel with THIS host's series and reports the price. */
  move(y: number): void {
    if (!this.dragging) return;
    const price = this.priceAt(y);
    if (!usable(price)) return;
    this.host.callbacks()?.onDrag(this.dragging, price);
  }

  /** Release (or pointer-cancel, which a touch interrupted by the OS sends instead). */
  up(y: number | null): void {
    if (!this.dragging) return;
    const id = this.dragging;
    this.dragging = null;
    // Restore FIRST: an exception in the drop handler must not leave a chart that
    // cannot be panned, because that failure is silent.
    this.host.setScroll(true);
    this.host.requestUpdate();
    const price = y === null ? null : this.priceAt(y);
    if (usable(price)) this.host.callbacks()?.onDrop?.(id, price);
  }

  private priceAt(y: number): number | null {
    const series = this.host.series();
    return series ? num(series.coordinateToPrice(y)) : null;
  }
}

/** Minimal listener surface, so a test can pass a stub instead of a real window. */
type Listeners = {
  addEventListener: (t: string, f: (e: PointerEvent) => void, capture?: boolean) => void;
  removeEventListener: (t: string, f: (e: PointerEvent) => void, capture?: boolean) => void;
};

/**
 * ONE POINTER GESTURE, ONE DRAG — the hub that makes that true (R4-8, high).
 *
 * `TicketPrimitive` and `LevelPrimitive` both live on the execution pane and both wired
 * `wireLegDrag(chart.chartElement(), …)`, i.e. two independent listener sets on ONE DOM
 * node, each driving its own `LegDrag` with its own `_savedScroll`. Measured consequence:
 * a single pointerdown near a shared price grabbed BOTH — the live position's stop and the
 * pre-trade ticket's leg moved together — and on release the two hosts restored the pan
 * flag from two different saved values. The second wrote back the `false` the first had
 * already stored, so `handleScroll` stayed off: the chart stopped panning, silently, with
 * no way to recover but a reload.
 *
 * SO THE ARBITRATION IS STRUCTURAL, not a flag either primitive checks. The hub keys on
 * the ELEMENT (a `WeakMap`, so a destroyed chart is collectable), installs exactly one set
 * of pointer listeners no matter how many drags register, and hands the gesture to the
 * FIRST registered drag whose `down` claims a leg. `move` and `up` reach only that drag,
 * so `setScroll(false)`/`setScroll(true)` are called on exactly one host per gesture and
 * cannot be interleaved. The two `_savedScroll` fields still exist — they belong to two
 * different charts as well as two primitives — but only the active host's is ever written,
 * which is what makes them no longer two states of one thing.
 *
 * REGISTRATION ORDER IS THE PRIORITY, and the pane fixes it: `ChartPane`'s constructor
 * attaches `LevelPrimitive` and `main.ts::initChart` attaches `TicketPrimitive` after it,
 * so a live position's resting leg wins a tie against a pre-trade line drawn at the same
 * price. That is the right way round — the resting order is the one that is real.
 */
type DragHub = {
  drags: LegDrag[];
  active: LegDrag | null;
  detach: () => void;
};

const HUBS = new WeakMap<HTMLElement, DragHub>();

/** The hub for this element, created (and wired) on first use. */
function hubFor(el: HTMLElement, win: Listeners): DragHub {
  const existing = HUBS.get(el);
  if (existing) return existing;

  const hub: DragHub = { drags: [], active: null, detach: () => {} };
  const yOf = (clientY: number) => clientY - el.getBoundingClientRect().top;

  const onDown = (e: PointerEvent) => {
    // ONE claimant. `down` has side effects (it suppresses the pane's pan), so the loop
    // must stop at the first success rather than asking every drag and picking a winner
    // afterwards — asking them all IS the defect.
    for (const drag of hub.drags) {
      if (drag.down(yOf(e.clientY))) {
        hub.active = drag;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }
  };
  const onMove = (e: PointerEvent) => {
    const drag = hub.active;
    if (!drag || !drag.dragging) return;
    drag.move(yOf(e.clientY));
    e.preventDefault();
  };
  const onUp = (e: PointerEvent) => {
    const drag = hub.active;
    if (!drag) return;
    // Cleared BEFORE `up`, so an exception in a drop handler cannot leave the hub holding
    // a drag that is no longer dragging — `LegDrag.up` restores the pan flag first for
    // the same reason, and this is that discipline one level out.
    hub.active = null;
    drag.up(yOf(e.clientY));
  };

  el.addEventListener('pointerdown', onDown, true);
  win.addEventListener('pointermove', onMove, true);
  win.addEventListener('pointerup', onUp, true);
  win.addEventListener('pointercancel', onUp, true);
  hub.detach = () => {
    el.removeEventListener('pointerdown', onDown, true);
    win.removeEventListener('pointermove', onMove, true);
    win.removeEventListener('pointerup', onUp, true);
    win.removeEventListener('pointercancel', onUp, true);
    HUBS.delete(el);
  };
  HUBS.set(el, hub);
  return hub;
}

/**
 * The DOM half: pointer events on one chart's canvas driving one `LegDrag` AT A TIME.
 *
 * `pointerdown` is bound to the CHART ELEMENT so a drag started elsewhere on the page
 * cannot move a level; move/up are bound to the WINDOW so a fast drag that leaves the
 * canvas still tracks and still releases — a lost `pointerup` would leave the chart
 * permanently unpannable.
 *
 * SEVERAL PRIMITIVES MAY CALL THIS FOR THE SAME ELEMENT and they share one listener set
 * (see `DragHub` above): that is the fix for R4-8, where two of them wired the node
 * independently and one gesture drove both.
 *
 * Returns the detach function for THIS registration; the listeners come down when the last
 * registration leaves.
 */
export function wireLegDrag(
  el: HTMLElement,
  drag: LegDrag,
  // `globalThis`, not `window`: this repo's vitest runs WITHOUT jsdom (no `environment`
  // in `vite.config.ts`), and a bare `window` reference throws at import-adjacent call
  // time — which would make every pane construction in a unit test fail for a reason
  // that has nothing to do with the thing under test.
  win: Listeners | null = (globalThis as { addEventListener?: unknown }).addEventListener
    ? (globalThis as unknown as Listeners)
    : null,
): () => void {
  // No listener surface = nothing to wire and nothing to detach. The drag ARITHMETIC is
  // still fully exercised in that case through `LegDrag` directly, which is how the
  // DOM-less tests drive it.
  if (!win) return () => {};
  const hub = hubFor(el, win);
  hub.drags.push(drag);
  return () => {
    const at = hub.drags.indexOf(drag);
    if (at >= 0) hub.drags.splice(at, 1);
    if (hub.active === drag) hub.active = null;
    // The listeners belong to the ELEMENT, so they only come down when no drag is left:
    // detaching them with a sibling still registered would kill that sibling's gesture.
    if (hub.drags.length === 0) hub.detach();
  };
}

/** How many drags are registered on this element — the R4-8 tooth's only observable.
 *
 *  Exported for the guard rather than for production: the defect is a COUNT (two listener
 *  sets on one node), and a test that could only observe behaviour would have to
 *  reconstruct a shared-price collision to see it. Nothing in `src/` calls this. */
export function dragsWiredOn(el: HTMLElement): number {
  return HUBS.get(el)?.drags.length ?? 0;
}
